import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import '@coinbase/onchainkit/styles.css';
import App from './app/app.jsx';
import { cleanupShareCache } from './services/share.service';
import { initializeStore } from './services/subscription.service';
import { useSubscriptionStore } from './stores/subscription.store';
import { installE2eBridge } from './dev/e2eBridge';
import { listenForNotificationClicks } from './services/web-push.service';
import { initSentry } from './services/sentry';

// Clean up old share cache files on app start
void cleanupShareCache();

// A browser notification click is counted by the page it opened (sw.js posts
// the push's type here). At boot, before the document finishes loading, so the
// message a click sent to a page it just opened is not missed.
listenForNotificationClicks();

// Initialize In-App Purchase store (only runs on native platforms)
initializeStore(
  (product, status) => {
    // Log for debugging in development
    if (import.meta.env.DEV) {
      console.log('IAP: Product/status update', { product, status });
    }

    // When a purchase is validated by the backend, refresh subscription state
    if (status === 'validated') {
      void useSubscriptionStore.getState().fetchSubscription();
    }
  },
  (error) => {
    console.error('IAP: Error', error);
    // Report errors to Sentry in production
    if (import.meta.env.PROD) {
      Sentry.captureException(error);
    }
  },
);

// Initialize Sentry for error monitoring in production (URLs scrubbed of
// secrets before anything is sent: services/sentry.ts)
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
  initSentry(import.meta.env.VITE_SENTRY_DSN, import.meta.env.MODE);
}

// Surface unhandled promise rejections in DEV with a tagged console warning so
// any new floating-promise leaks past `fireAndForget(...)` are immediately
// visible during local development. Sentry's globalHandlersIntegration captures
// these in production automatically.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.warn(
      '[unhandledrejection] A Promise rejected without a handler. ' +
        'If this came from fire-and-forget code, wrap it with fireAndForget() ' +
        'or attach a .catch().',
      event.reason,
    );
  });
}

// The Playwright specs' way into the app's modules (DEV only; dropped from
// production builds). See src/dev/e2eBridge.ts.
installE2eBridge();

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
